import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi } from '../api/auth';

interface TimerContextValue {
  isSiteActive: boolean;
  countdownEndTime: Date | null;
  timeRemaining: number; // seconds
  isExpired: boolean;
  loading: boolean;
  refreshStatus: () => Promise<void>;
}

const TimerContext = createContext<TimerContextValue | undefined>(undefined);

// Sync with server every 30 seconds
const SYNC_INTERVAL = 30000;

export function TimerProvider({ children }: { children: ReactNode }) {
  // Default isSiteActive to true during loading to prevent premature redirects
  const [isSiteActive, setIsSiteActive] = useState(true);
  const [countdownEndTime, setCountdownEndTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [clockOffset, setClockOffset] = useState(0); // Server time - local time
  const [loading, setLoading] = useState(true);

  const refreshStatus = useCallback(async () => {
    try {
      const status = await authApi.getStatus();

      // Calculate clock offset
      const serverTime = new Date(status.serverTime).getTime();
      const localTime = Date.now();
      setClockOffset(serverTime - localTime);

      setIsSiteActive(status.isSiteActive);

      if (status.countdownEndTime) {
        setCountdownEndTime(new Date(status.countdownEndTime));
        // Also set timeRemaining from server to avoid race condition
        setTimeRemaining(status.timeRemaining ?? 0);
      } else {
        setCountdownEndTime(null);
        setTimeRemaining(0);
      }
    } catch (error) {
      console.error('[TimerContext] Failed to refresh status:', error);
      // Don't change isSiteActive on error - keep it as-is
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and periodic sync
  useEffect(() => {
    refreshStatus();

    const interval = setInterval(refreshStatus, SYNC_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshStatus]);

  // Update time remaining every second
  useEffect(() => {
    if (!countdownEndTime) {
      setTimeRemaining(0);
      return;
    }

    const updateRemaining = () => {
      const adjustedNow = Date.now() + clockOffset;
      const remaining = Math.max(0, Math.floor((countdownEndTime.getTime() - adjustedNow) / 1000));
      setTimeRemaining(remaining);

      // Auto-deactivate when expired
      if (remaining === 0 && isSiteActive) {
        setIsSiteActive(false);
      }
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);
    return () => clearInterval(interval);
  }, [countdownEndTime, clockOffset, isSiteActive]);

  // Only consider expired if we have a countdown end time AND time has run out
  // This prevents false positives when timeRemaining is 0 due to initial state
  const isExpired = isSiteActive && countdownEndTime !== null && timeRemaining === 0;

  return (
    <TimerContext.Provider
      value={{
        isSiteActive,
        countdownEndTime,
        timeRemaining,
        isExpired,
        loading,
        refreshStatus,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}

export default TimerContext;
